import { useState, useRef, useEffect, useCallback } from 'react';
import { Canvas, Circle, Line, Text, FabricImage } from 'fabric';
import { Pencil, Info, RotateCcw, Undo, Eye, EyeOff } from 'lucide-react';
import { useWizard } from '../../context/WizardContext';
import { useGrid } from '../../context/GridContext';
import { useBreakpoint } from '../../hooks/useBreakpoint';
import { useHasCoarsePointer } from '../../hooks/useHasCoarsePointer';
import { useElementSize } from '../../hooks/useElementSize';
import { getEventCoords, applyTouchActionNone, attachPinchZoom } from '../../utils/canvasTouchUtils';
import { drawViewportGrid } from '../../utils/drawViewportGrid';
import { attachMeasurementLabelScaling } from '../../utils/measurementLabelScaling';

// Padding inside the canvas reserved around the background image on first fit.
const IMAGE_FIT_PADDING_PX = 40;
// Floor for canvas dimensions so the surface stays usable on tiny viewports.
const MIN_CANVAS_DIMENSION_PX = 200;
import {
  addVertex,
  removeLastVertex,
  isNearPoint,
  calculateDistance,
} from '../Editor/tools/PolygonTool';
import {
  CANONICAL_PIXELS_PER_METER,
  imageScaleForCanonicalDisplay,
} from './inputs/scaleCalibrationUtils';
import GardenDimensionsPanel from './GardenDimensionsPanel';
import SnapToggleButton from './SnapToggleButton';

/**
 * Step3MobileView — shown on mobile instead of Fabric.js canvas.
 * Two sliders for approximate garden dimensions; polygon drawing is deferred to desktop.
 */
const Step3MobileView = ({ savedRect, updateOutline, nextStep }) => {
  const [width, setWidth] = useState(savedRect.width);
  const [depth, setDepth] = useState(savedRect.depth);

  // On mobile, polygon drawing is always skipped in favour of rect dimensions.
  // Set skipped:true immediately so the "Weiter" button is enabled on mount.
  useEffect(() => {
    updateOutline({ skipped: true, rect: { width, depth } });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleWidthChange = (e) => {
    const val = Number(e.target.value);
    setWidth(val);
    updateOutline({ rect: { width: val, depth }, skipped: true });
  };

  const handleDepthChange = (e) => {
    const val = Number(e.target.value);
    setDepth(val);
    updateOutline({ rect: { width, depth: val }, skipped: true });
  };

  const handleSkip = () => {
    updateOutline({ skipped: true });
    nextStep();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Pencil size={20} className="text-gray-700" />
        <h2 className="text-xl font-bold text-gray-900">Gartenmaße</h2>
      </div>

      <p className="text-sm text-gray-600">
        Gib ungefähre Maße deines Gartens ein.
        Am Desktop kannst du den Umriss später genau einzeichnen.
      </p>

      <div className="space-y-5">
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Breite: <span className="font-bold text-emerald-600">{width} m</span>
          </label>
          <input
            type="range"
            min={1}
            max={50}
            value={width}
            onChange={handleWidthChange}
            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-emerald-600"
          />
          <div className="flex justify-between text-xs text-gray-400">
            <span>1 m</span>
            <span>50 m</span>
          </div>
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Tiefe: <span className="font-bold text-emerald-600">{depth} m</span>
          </label>
          <input
            type="range"
            min={1}
            max={50}
            value={depth}
            onChange={handleDepthChange}
            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-emerald-600"
          />
          <div className="flex justify-between text-xs text-gray-400">
            <span>1 m</span>
            <span>50 m</span>
          </div>
        </div>
      </div>

      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
        <p className="text-sm text-emerald-800">
          Ungefähre Fläche: <strong>{width * depth} m²</strong>
        </p>
      </div>

      <button
        onClick={handleSkip}
        className="text-sm text-gray-500 underline"
      >
        Überspringen — später am Desktop einzeichnen
      </button>
    </div>
  );
};

/**
 * Step3GardenOutline Component
 *
 * Wizard Step 3: Draw garden boundary with polygon tool
 * Design based on: Issue #12 and docs/plans/PLAN_INITIAL_SETUP_WIZARD.md
 *
 * Features:
 * - Click-to-add-vertex polygon drawing
 * - Background image display (from Step 2, optional)
 * - Grid system with snap-to-grid
 * - Crosshair cursor for precise placement
 * - Visual feedback: vertex dots, connecting lines, live preview
 * - Auto-close when clicking near first vertex
 * - Undo last point and clear/restart functionality
 * - Vertex editing after polygon is closed
 *
 * Performance optimizations (#163):
 * - Transient canvas state (mousePosition, nearFirstVertex) in refs, not React state
 * - Preview line/label reused via .set() instead of remove/recreate
 * - Preview updates directly in mouse:move handler (no useEffect round-trip)
 * - Incremental vertex rendering (only new objects added, not full rebuild)
 * - Live label updates during vertex drag
 * - skipTargetFind during drawing mode
 * - requestRenderAll() instead of renderAll() where possible
 */
const Step3GardenOutline = () => {
  const { gardenData, updateOutline, nextStep } = useWizard();
  const { gridEnabled, snapEnabled } = useGrid();
  const { isMobile } = useBreakpoint();
  const isCoarsePointer = useHasCoarsePointer();

  // Use a finer grid for outline drawing (10cm instead of default)
  const gridSize = 10;

  const canvasRef = useRef(null);
  const fabricCanvasRef = useRef(null);
  const [canvasWrapperRef, wrapperSize] = useElementSize();
  const backgroundImageRef = useRef(null);
  const vertexMarkersRef = useRef([]);
  const edgeLinesRef = useRef([]);
  const edgeLabelsRef = useRef([]);
  const previewLineRef = useRef(null);
  const previewLabelRef = useRef(null);

  // Ref for vertices used in Fabric.js event handlers (avoids stale closures)
  const verticesRef = useRef(gardenData.outline?.vertices || []);

  // Imperative handle to GardenDimensionsPanel — Step3 calls .tick() per RAF
  // during vertex-drag so the panel re-renders without re-rendering Step3 (#454).
  const dimensionsPanelRef = useRef(null);
  // Pending requestAnimationFrame id during a drag — cleared on dispose to
  // avoid touching an unmounted panel.
  const dragRafIdRef = useRef(null);

  // Transient canvas state — refs instead of React state (P1, P3, P6)
  const mousePositionRef = useRef(null);
  const touchDrawPendingRef = useRef(false);
  const nearFirstVertexRef = useRef(false);

  const [vertices, setVertices] = useState(gardenData.outline?.vertices || []);
  const [isClosed, setIsClosed] = useState(gardenData.outline?.isClosed || false);
  const [isDrawing, setIsDrawing] = useState(true);
  const [ctrlPressed, setCtrlPressed] = useState(false);
  const [showBackgroundImage, setShowBackgroundImage] = useState(true);
  const [nearFirstVertex, setNearFirstVertex] = useState(false);
  const [isPanning, setIsPanning] = useState(false);
  const [spacePressed, setSpacePressed] = useState(false);
  const lastPosRef = useRef({ x: 0, y: 0 });

  // Rebuild trigger — increment to force renderPolygonPreview via useEffect
  const [rebuildTrigger, setRebuildTrigger] = useState(0);

  // Refs for Fabric.js event handlers (avoids stale closures)
  const isClosedRef = useRef(isClosed);
  const ctrlPressedRef = useRef(ctrlPressed);
  const snapEnabledRef = useRef(snapEnabled);

  // Stable refs for functions from context/hooks that change reference on
  // WizardContext re-renders (step navigation). Without these, every step
  // change cascades through useCallback deps → rebuild useEffect fires.
  const updateOutlineRef = useRef(updateOutline);
  const getPixelsPerMeterRef = useRef(null);

  // Keep refs in sync with React state / latest function identities. Written in
  // effects (not during render) so the refs rule isn't violated; the refs are only
  // read inside Fabric event handlers, which run after commit.
  useEffect(() => { verticesRef.current = vertices; }, [vertices]);
  useEffect(() => { isClosedRef.current = isClosed; }, [isClosed]);
  useEffect(() => { ctrlPressedRef.current = ctrlPressed; }, [ctrlPressed]);
  useEffect(() => { snapEnabledRef.current = snapEnabled; }, [snapEnabled]);
  useEffect(() => { updateOutlineRef.current = updateOutline; }, [updateOutline]);

  /**
   * Get pixelsPerMeter for label calculations.
   *
   * When the calibration includes nativePixelsPerMeter (new code path), the
   * background image is rendered at canonical scale (1 m = 100 canvas px), so
   * polygon edge measurements use CANONICAL_PIXELS_PER_METER directly. Older
   * calibrations without nativePixelsPerMeter fall back to calibration.scale,
   * which is correct only when Step 2 and Step 3 use identical canvas sizes.
   */
  const getPixelsPerMeter = useCallback(() => {
    const calib = gardenData.backgroundImage?.calibration;
    if (!gardenData.backgroundImage?.dataUrl || !calib?.isCalibrated) return 100;
    if (calib.nativePixelsPerMeter) return CANONICAL_PIXELS_PER_METER;
    return calib.scale || 100;
  }, [gardenData.backgroundImage]);
  useEffect(() => { getPixelsPerMeterRef.current = getPixelsPerMeter; }, [getPixelsPerMeter]);

  /**
   * Initialize Fabric.js canvas. Seeded from a viewport approximation
   * (the CSS wrapper sizes the canvas to ~65vh) so the first paint is close
   * to the final size; the resize effect below snaps to the measured wrapper
   * dimensions (#450 — was hardcoded 960×600 which overflowed the dialog).
   */
  useEffect(() => {
    if (!canvasRef.current || fabricCanvasRef.current) return;

    const initWidth = Math.max(
      MIN_CANVAS_DIMENSION_PX,
      Math.min(1400, Math.round(window.innerWidth * 0.85)),
    );
    const initHeight = Math.max(
      MIN_CANVAS_DIMENSION_PX,
      Math.min(600, Math.round(window.innerHeight * 0.55)),
    );

    const canvas = new Canvas(canvasRef.current, {
      width: initWidth,
      height: initHeight,
      // No Fabric backgroundColor: the canvas stays transparent so the grid,
      // drawn on the `before:render` hook, is not painted over. The Gray-50
      // backdrop comes from the wrapper element's CSS (see canvasWrapperRef).
      selection: false,
      defaultCursor: 'crosshair',
      hoverCursor: 'crosshair',
      skipTargetFind: true, // P6: Disable hit detection during drawing
    });

    fabricCanvasRef.current = canvas;
    applyTouchActionNone(canvas);

    // Create reusable preview objects (P2: create once, update via .set())
    const previewLine = new Line([0, 0, 0, 0], {
      stroke: '#10B981',
      strokeWidth: 2,
      strokeDashArray: [5, 5],
      selectable: false,
      evented: false,
      visible: false,
      data: { type: 'preview-line' },
    });
    const previewLabel = new Text('', {
      fontSize: 12,
      fontFamily: 'Inter, sans-serif',
      fontWeight: 'bold',
      fill: '#10B981',
      backgroundColor: '#FFFFFF',
      padding: 3,
      originX: 'center',
      originY: 'center',
      selectable: false,
      evented: false,
      visible: false,
      // Counter-scaled when zoomed out; the tool owns show/hide, so opt out of
      // the hide-below-threshold behavior (see measurementLabelScaling).
      data: { isMeasurementLabel: true, ownVisibility: true },
    });
    canvas.add(previewLine);
    canvas.add(previewLabel);
    previewLineRef.current = previewLine;
    previewLabelRef.current = previewLabel;

    // Keep edge/segment measurement labels readable when zoomed out.
    const detachLabelScaling = attachMeasurementLabelScaling(canvas);

    return () => {
      if (dragRafIdRef.current != null) {
        cancelAnimationFrame(dragRafIdRef.current);
        dragRafIdRef.current = null;
      }
      detachLabelScaling();
      if (fabricCanvasRef.current) {
        fabricCanvasRef.current.dispose();
        fabricCanvasRef.current = null;
        previewLineRef.current = null;
        previewLabelRef.current = null;
      }
    };
  }, []);

  /**
   * Keep the canvas surface in sync with the wrapper element (#450).
   * Triggers a grid rebuild via the grid effect (it depends on wrapperSize).
   * Polygon vertices and the background image keep their pixel coordinates;
   * the user can pan/zoom if the new viewport hides them.
   */
  useEffect(() => {
    const canvas = fabricCanvasRef.current;
    if (!canvas || !wrapperSize) return;
    const width = Math.max(MIN_CANVAS_DIMENSION_PX, Math.round(wrapperSize.width));
    const height = Math.max(MIN_CANVAS_DIMENSION_PX, Math.round(wrapperSize.height));
    if (canvas.width === width && canvas.height === height) return;
    canvas.setDimensions({ width, height });
    canvas.requestRenderAll();
  }, [wrapperSize]);

  /**
   * Load background image if uploaded in Step 2
   */
  useEffect(() => {
    if (!fabricCanvasRef.current || !gardenData.backgroundImage?.dataUrl) return;

    const canvas = fabricCanvasRef.current;

    FabricImage.fromURL(gardenData.backgroundImage.dataUrl, {
      crossOrigin: 'anonymous',
    }).then((fabricImage) => {
      // If calibration provides nativePixelsPerMeter, render the image so that
      // 1 m on the original image equals CANONICAL_PIXELS_PER_METER on canvas.
      // Otherwise fall back to fit-to-canvas (legacy / uncalibrated images).
      const nativePxPerM = gardenData.backgroundImage.calibration?.nativePixelsPerMeter;
      const maxWidth = Math.max(1, canvas.width - IMAGE_FIT_PADDING_PX);
      const maxHeight = Math.max(1, canvas.height - IMAGE_FIT_PADDING_PX);
      const fitScale = Math.min(maxWidth / fabricImage.width, maxHeight / fabricImage.height);
      const scale = nativePxPerM ? imageScaleForCanonicalDisplay(nativePxPerM) : fitScale;

      // Use saved position and rotation from Step 2, or defaults (upper-left)
      const savedPosition = gardenData.backgroundImage.position || { x: 20, y: 20 };
      const savedRotation = gardenData.backgroundImage.rotation || 0;

      fabricImage.set({
        scaleX: scale,
        scaleY: scale,
        left: savedPosition.x,
        top: savedPosition.y,
        angle: savedRotation,
        opacity: gardenData.backgroundImage.transparency / 100,
        selectable: false,
        evented: false,
        hasControls: false,
        hasBorders: false,
        originX: 'left',  // Match ScaleCalibrator origin
        originY: 'top',
      });

      canvas.add(fabricImage);
      canvas.sendObjectToBack(fabricImage);
      backgroundImageRef.current = fabricImage;

      canvas.requestRenderAll();
    });

    return () => {
      if (backgroundImageRef.current) {
        canvas.remove(backgroundImageRef.current);
        backgroundImageRef.current = null;
      }
    };
  }, [gardenData.backgroundImage?.dataUrl, gardenData.backgroundImage?.position, gardenData.backgroundImage?.rotation, gardenData.backgroundImage?.transparency, gardenData.backgroundImage?.calibration?.nativePixelsPerMeter]);

  /**
   * Toggle background image visibility
   */
  useEffect(() => {
    if (backgroundImageRef.current) {
      backgroundImageRef.current.set({
        visible: showBackgroundImage,
      });
      fabricCanvasRef.current?.requestRenderAll();
    }
  }, [showBackgroundImage]);

  /**
   * Update background image transparency
   */
  useEffect(() => {
    if (backgroundImageRef.current && gardenData.backgroundImage?.transparency !== undefined) {
      backgroundImageRef.current.set({
        opacity: gardenData.backgroundImage.transparency / 100,
      });
      fabricCanvasRef.current?.requestRenderAll();
    }
  }, [gardenData.backgroundImage]);

  /**
   * Draw grid via the Fabric.js `before:render` hook. The grid is computed from
   * the live viewportTransform each frame, so it stays continuous and fills the
   * full visible surface at any zoom/pan (matching the 2D editor's GridOverlay).
   * The earlier static Line-object grid was drawn in world space and collapsed
   * into the top-left corner when zooming out.
   *
   * Depends on wrapperSize so the effect re-runs once the canvas exists (the
   * canvas lives in a ref; wrapperSize updates right after mount via #450).
   */
  useEffect(() => {
    const canvas = fabricCanvasRef.current;
    if (!canvas || !gridEnabled) return;

    const drawGrid = ({ ctx }) => {
      drawViewportGrid(ctx, canvas, { gridSize });
    };

    canvas.on('before:render', drawGrid);
    canvas.requestRenderAll();

    return () => {
      canvas.off('before:render', drawGrid);
      if (typeof canvas.requestRenderAll === 'function') {
        canvas.requestRenderAll();
      }
    };
  }, [gridEnabled, wrapperSize, gridSize]);


  /**
   * Create vertex marker (circle) after placement
   */
  const createVertexMarker = useCallback((x, y, index, closed) => {
    const isFirst = index === 0;
    // Larger hit target on touch so outline points are grabbable with a finger (#747).
    const radius = isFirst ? (isCoarsePointer ? 13 : 8) : (isCoarsePointer ? 11 : 6);

    const marker = new Circle({
      radius,
      fill: '#10B981',
      stroke: '#FFFFFF',
      strokeWidth: 2,
      left: x,
      top: y,
      originX: 'center',
      originY: 'center',
      selectable: closed, // Only selectable after polygon is closed
      hasControls: false,
      hasBorders: false,
      lockRotation: true,
      hoverCursor: closed ? 'move' : 'default',
      data: {
        type: 'vertex-marker',
        vertexIndex: index,
      },
    });

    return marker;
  }, [isCoarsePointer]);

  /**
   * Update edges and labels while dragging a vertex (P5: live label updates)
   */
  const updateEdgesWhileDragging = useCallback((dragIndex, newX, newY) => {
    if (!fabricCanvasRef.current) return;

    const verts = verticesRef.current;
    const numVerts = verts.length;
    const pixelsPerMeter = getPixelsPerMeterRef.current();

    // Update the edge BEFORE this vertex (prevIndex → dragIndex).
    // Always runs: when dragIndex === 0 the previous edge is the closing edge
    // (numVerts - 1), so wrap-around indexing covers both cases.
    {
      const lineIndex = dragIndex > 0 ? dragIndex - 1 : numVerts - 1;
      const prevVertexIndex = dragIndex > 0 ? dragIndex - 1 : numVerts - 1;
      const line = edgeLinesRef.current[lineIndex];
      const label = edgeLabelsRef.current[lineIndex];

      if (line) {
        line.set({ x2: newX, y2: newY });
      }
      if (label) {
        const prev = verts[prevVertexIndex];
        const midX = (prev.x + newX) / 2;
        const midY = (prev.y + newY) / 2;
        const dist = calculateDistance(prev, { x: newX, y: newY });
        const lengthMeters = dist / pixelsPerMeter;
        label.set({ left: midX, top: midY - 15, text: `${lengthMeters.toFixed(2)} m` });
      }
    }

    // Update the edge AFTER this vertex (dragIndex → nextIndex)
    {
      const lineIndex = dragIndex;
      const nextVertexIndex = dragIndex < numVerts - 1 ? dragIndex + 1 : 0;
      const line = edgeLinesRef.current[lineIndex];
      const label = edgeLabelsRef.current[lineIndex];

      if (line) {
        line.set({ x1: newX, y1: newY });
      }
      if (label) {
        const next = verts[nextVertexIndex];
        const midX = (newX + next.x) / 2;
        const midY = (newY + next.y) / 2;
        const dist = calculateDistance({ x: newX, y: newY }, next);
        const lengthMeters = dist / pixelsPerMeter;
        label.set({ left: midX, top: midY - 15, text: `${lengthMeters.toFixed(2)} m` });
      }
    }

    fabricCanvasRef.current.requestRenderAll();
  }, []); // Stable: reads all mutable values from refs

  /**
   * Full rebuild of polygon preview — used for: initial load, undo, close.
   * For normal vertex addition, use addVertexToCanvas() instead.
   *
   * Reads vertices/isClosed/snap/ctrl from REFS (not React state) so the
   * useCallback reference stays stable during normal drawing — this prevents
   * the rebuild useEffect from firing on every vertex addition (Bug 1 fix).
   */
  const renderPolygonPreview = useCallback(() => {
    if (!fabricCanvasRef.current) return;

    const canvas = fabricCanvasRef.current;
    const verts = verticesRef.current;
    const closed = isClosedRef.current;

    // Optimization: Disable auto-render while adding/removing multiple objects
    canvas.renderOnAddRemove = false;

    // Clear existing markers, edges, and labels
    vertexMarkersRef.current.forEach((marker) => canvas.remove(marker));
    edgeLinesRef.current.forEach((line) => canvas.remove(line));
    edgeLabelsRef.current.forEach((label) => canvas.remove(label));
    vertexMarkersRef.current = [];
    edgeLinesRef.current = [];
    edgeLabelsRef.current = [];

    const pixelsPerMeter = getPixelsPerMeterRef.current();

    // Draw edges (connecting lines) and labels
    for (let i = 0; i < verts.length - 1; i++) {
      const v1 = verts[i];
      const v2 = verts[i + 1];

      const line = new Line([v1.x, v1.y, v2.x, v2.y], {
        stroke: '#10B981',
        strokeWidth: 2,
        selectable: false,
        evented: false,
        data: { type: 'polygon-edge' },
      });
      canvas.add(line);
      edgeLinesRef.current.push(line);

      const pixelLength = calculateDistance(v1, v2);
      const lengthMeters = pixelLength / pixelsPerMeter;
      const midX = (v1.x + v2.x) / 2;
      const midY = (v1.y + v2.y) / 2;

      const label = new Text(`${lengthMeters.toFixed(2)} m`, {
        left: midX,
        top: midY - 15,
        fontSize: 12,
        fontFamily: 'Inter, sans-serif',
        fontWeight: 'bold',
        fill: '#10B981',
        backgroundColor: '#FFFFFF',
        padding: 3,
        originX: 'center',
        originY: 'center',
        selectable: false,
        evented: false,
        data: { isMeasurementLabel: true },
      });
      canvas.add(label);
      edgeLabelsRef.current.push(label);
    }

    // If closed, draw final edge and label
    if (closed && verts.length >= 3) {
      const v1 = verts[verts.length - 1];
      const v2 = verts[0];

      const line = new Line([v1.x, v1.y, v2.x, v2.y], {
        stroke: '#10B981',
        strokeWidth: 2,
        selectable: false,
        evented: false,
        data: { type: 'polygon-edge' },
      });
      canvas.add(line);
      edgeLinesRef.current.push(line);

      const pixelLength = calculateDistance(v1, v2);
      const lengthMeters = pixelLength / pixelsPerMeter;
      const midX = (v1.x + v2.x) / 2;
      const midY = (v1.y + v2.y) / 2;

      const label = new Text(`${lengthMeters.toFixed(2)} m`, {
        left: midX,
        top: midY - 15,
        fontSize: 12,
        fontFamily: 'Inter, sans-serif',
        fontWeight: 'bold',
        fill: '#10B981',
        backgroundColor: '#FFFFFF',
        padding: 3,
        originX: 'center',
        originY: 'center',
        selectable: false,
        evented: false,
        data: { isMeasurementLabel: true },
      });
      canvas.add(label);
      edgeLabelsRef.current.push(label);
    }

    // Draw vertex markers
    verts.forEach((vertex, index) => {
      const marker = createVertexMarker(vertex.x, vertex.y, index, closed);
      canvas.add(marker);
      vertexMarkersRef.current.push(marker);

      // If polygon is closed, allow dragging vertices
      if (closed) {
        marker.on('moving', () => {
          // Read snap/ctrl from refs (avoids stale closures)
          if (snapEnabledRef.current && !ctrlPressedRef.current) {
            const snappedX = Math.round(marker.left / gridSize) * gridSize;
            const snappedY = Math.round(marker.top / gridSize) * gridSize;
            marker.set({
              left: snappedX,
              top: snappedY,
            });
          }

          // Update vertex in ref for edge calculations
          const newVerts = [...verticesRef.current];
          newVerts[index] = { x: marker.left, y: marker.top };
          verticesRef.current = newVerts;

          // Update edges and labels in real-time (P5)
          updateEdgesWhileDragging(index, marker.left, marker.top);

          // Live DOM-panel update (#454). RAF-coalesced so we render at most
          // once per frame even when Fabric.js fires `moving` faster than 60Hz.
          // Step3 itself does NOT re-render — only GardenDimensionsPanel ticks
          // via the imperative ref. This keeps the useCallback/useEffect
          // deps on `vertices` stable during drag.
          if (dragRafIdRef.current == null) {
            dragRafIdRef.current = requestAnimationFrame(() => {
              dragRafIdRef.current = null;
              dimensionsPanelRef.current?.tick();
            });
          }
        });

        marker.on('modified', () => {
          if (snapEnabledRef.current && !ctrlPressedRef.current) {
            const snappedX = Math.round(marker.left / gridSize) * gridSize;
            const snappedY = Math.round(marker.top / gridSize) * gridSize;
            marker.set({
              left: snappedX,
              top: snappedY,
            });
          }

          const newVertices = [...verticesRef.current];
          newVertices[index] = { x: marker.left, y: marker.top };
          setVertices(newVertices);
          updateOutlineRef.current({
            vertices: newVertices,
            isClosed: true,
          });
        });
      }
    });

    // Set skipTargetFind based on closed state (Bug 2 fix: enables vertex
    // selection when loading a saved closed polygon after step navigation)
    canvas.skipTargetFind = !closed;

    // Re-enable auto-render and render once
    canvas.renderOnAddRemove = true;
    canvas.requestRenderAll();
  }, [createVertexMarker, updateEdgesWhileDragging, gridSize]); // Stable: reads updateOutline/getPixelsPerMeter from refs

  /**
   * Add a single vertex to canvas incrementally (P2: no full rebuild)
   */
  const addVertexToCanvas = useCallback((vertex, index, allVertices) => {
    if (!fabricCanvasRef.current) return;

    const canvas = fabricCanvasRef.current;
    const pixelsPerMeter = getPixelsPerMeterRef.current();

    canvas.renderOnAddRemove = false;

    // 1. Create edge line from previous vertex to new vertex
    if (index > 0) {
      const prev = allVertices[index - 1];
      const line = new Line([prev.x, prev.y, vertex.x, vertex.y], {
        stroke: '#10B981',
        strokeWidth: 2,
        selectable: false,
        evented: false,
        data: { type: 'polygon-edge' },
      });
      canvas.add(line);
      edgeLinesRef.current.push(line);

      // Create label
      const pixelLength = calculateDistance(prev, vertex);
      const lengthMeters = pixelLength / pixelsPerMeter;
      const midX = (prev.x + vertex.x) / 2;
      const midY = (prev.y + vertex.y) / 2;

      const label = new Text(`${lengthMeters.toFixed(2)} m`, {
        left: midX,
        top: midY - 15,
        fontSize: 12,
        fontFamily: 'Inter, sans-serif',
        fontWeight: 'bold',
        fill: '#10B981',
        backgroundColor: '#FFFFFF',
        padding: 3,
        originX: 'center',
        originY: 'center',
        selectable: false,
        evented: false,
        data: { isMeasurementLabel: true },
      });
      canvas.add(label);
      edgeLabelsRef.current.push(label);
    }

    // 2. Create vertex marker
    const marker = createVertexMarker(vertex.x, vertex.y, index, false);
    canvas.add(marker);
    vertexMarkersRef.current.push(marker);

    canvas.renderOnAddRemove = true;
    canvas.requestRenderAll();
  }, [createVertexMarker]); // Stable: reads getPixelsPerMeter from ref

  /**
   * Full rebuild of polygon on canvas.
   * Triggered by: initial mount, rebuildTrigger (undo, close), or renderPolygonPreview
   * reference change (rare — only when createVertexMarker/gridSize/etc. change).
   *
   * NOT triggered by: normal vertex addition (handled by addVertexToCanvas).
   */
  useEffect(() => {
    renderPolygonPreview();

    // Cleanup function to prevent object accumulation
    return () => {
      if (!fabricCanvasRef.current) return;
      const canvas = fabricCanvasRef.current;

      vertexMarkersRef.current.forEach((marker) => canvas.remove(marker));
      edgeLinesRef.current.forEach((line) => canvas.remove(line));
      edgeLabelsRef.current.forEach((label) => canvas.remove(label));
      vertexMarkersRef.current = [];
      edgeLinesRef.current = [];
      edgeLabelsRef.current = [];
    };
  }, [renderPolygonPreview, rebuildTrigger]);

  /**
   * Update preview line/label directly via .set() (P2: no object recreation)
   * Called from mouse:move handler — no useEffect needed.
   */
  const updatePreviewLine = useCallback((pointer) => {
    if (!fabricCanvasRef.current || !previewLineRef.current || !previewLabelRef.current) return;

    const verts = verticesRef.current;
    if (verts.length === 0) {
      previewLineRef.current.set({ visible: false });
      previewLabelRef.current.set({ visible: false });
      fabricCanvasRef.current.requestRenderAll();
      return;
    }

    const lastVertex = verts[verts.length - 1];
    const pixelsPerMeter = getPixelsPerMeterRef.current();

    // Apply snap-to-grid to preview position (read from refs for latest values)
    const snappedX = snapEnabledRef.current && !ctrlPressedRef.current
      ? Math.round(pointer.x / gridSize) * gridSize
      : pointer.x;
    const snappedY = snapEnabledRef.current && !ctrlPressedRef.current
      ? Math.round(pointer.y / gridSize) * gridSize
      : pointer.y;

    // Update preview line via .set() (no remove/recreate)
    previewLineRef.current.set({
      x1: lastVertex.x,
      y1: lastVertex.y,
      x2: snappedX,
      y2: snappedY,
      visible: true,
    });

    // Update preview label
    const pixelLength = calculateDistance(lastVertex, { x: snappedX, y: snappedY });
    const lengthMeters = pixelLength / pixelsPerMeter;
    const midX = (lastVertex.x + snappedX) / 2;
    const midY = (lastVertex.y + snappedY) / 2;

    previewLabelRef.current.set({
      left: midX,
      top: midY - 15,
      text: `${lengthMeters.toFixed(2)} m`,
      visible: true,
    });

    fabricCanvasRef.current.requestRenderAll();
  }, [gridSize]); // Stable: reads snap/ctrl/pixelsPerMeter from refs

  /**
   * Hide preview line/label (called when polygon is closed or drawing cleared)
   */
  const hidePreview = useCallback(() => {
    if (previewLineRef.current) {
      previewLineRef.current.set({ visible: false });
    }
    if (previewLabelRef.current) {
      previewLabelRef.current.set({ visible: false });
    }
    if (fabricCanvasRef.current) {
      fabricCanvasRef.current.requestRenderAll();
    }
  }, []);

  /**
   * Handle canvas mouse move (update preview) — P3: no React state, direct canvas update
   */
  const handleCanvasMouseMove = useCallback(
    (e) => {
      if (!fabricCanvasRef.current || isClosed) return;

      const canvas = fabricCanvasRef.current;
      const pointer = canvas.getScenePoint(e.e);

      // Store in ref (no React re-render)
      mousePositionRef.current = pointer;

      // Update preview line directly on canvas (P2 + P3)
      updatePreviewLine(pointer);

      // Check if near first vertex for auto-close hint
      const verts = verticesRef.current;
      if (verts.length >= 3) {
        const wasNear = nearFirstVertexRef.current;
        const isNear = isNearPoint(pointer, verts[0], 10);
        nearFirstVertexRef.current = isNear;

        // Only trigger React re-render when nearFirstVertex changes (for DOM hint text)
        if (wasNear !== isNear) {
          setNearFirstVertex(isNear);
        }
      } else if (nearFirstVertexRef.current) {
        nearFirstVertexRef.current = false;
        setNearFirstVertex(false);
      }
    },
    [isClosed, updatePreviewLine]
  );

  /**
   * Handle canvas click (add vertex)
   */
  const handleCanvasClick = useCallback(
    (e, explicitPointer) => {
      if (!fabricCanvasRef.current || isClosed) return;

      const canvas = fabricCanvasRef.current;
      // On touch, placement is committed on lift with the last finger position passed
      // in explicitly; on mouse it's the click point. (#747)
      const pointer = explicitPointer ?? canvas.getScenePoint(e.e);

      // Check if clicking on first vertex marker to close polygon
      if (e.target && e.target.data?.type === 'vertex-marker' && e.target.data?.vertexIndex === 0 && vertices.length >= 3) {
        // Clicking on first vertex - close the polygon
        setIsClosed(true);
        setIsDrawing(false);
        setRebuildTrigger((v) => v + 1); // Force full rebuild (adds closing edge, makes markers draggable)
        hidePreview();

        updateOutline({
          vertices,
          isClosed: true,
        });

        return;
      }

      // Ignore clicks on other markers
      if (e.target && e.target.data?.type === 'vertex-marker') return;

      const result = addVertex(vertices, pointer, ctrlPressed, gridSize, snapEnabled);

      if (result.isDuplicate) {
        // Don't add duplicate vertex
        return;
      }

      if (result.shouldClose) {
        // Close the polygon
        setVertices(result.vertices);
        setIsClosed(true);
        setIsDrawing(false);
        setRebuildTrigger((v) => v + 1); // Force full rebuild
        hidePreview();

        // Update wizard state
        updateOutline({
          vertices: result.vertices,
          isClosed: true,
        });

        return;
      }

      // Add new vertex incrementally to canvas (P4)
      const newVertex = result.vertices[result.vertices.length - 1];
      addVertexToCanvas(newVertex, result.vertices.length - 1, result.vertices);

      // Update React state
      setVertices(result.vertices);

      // Update wizard state
      updateOutline({
        vertices: result.vertices,
        isClosed: false,
      });
    },
    [vertices, isClosed, ctrlPressed, gridSize, snapEnabled, updateOutline, addVertexToCanvas, hidePreview]
  );

  /**
   * Handle panning with spacebar and single-finger touch
   */
  const handlePanMouseDown = useCallback((opt) => {
    if (spacePressed) {
      setIsPanning(true);
      const { x, y } = getEventCoords(opt.e);
      lastPosRef.current = { x, y };
    }
  }, [spacePressed]);

  const handlePanMouseMove = useCallback((opt) => {
    if (!isPanning) return;
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;
    const { x, y } = getEventCoords(opt.e);
    if (x == null || Number.isNaN(x) || y == null || Number.isNaN(y)) return;
    const dx = x - lastPosRef.current.x;
    const dy = y - lastPosRef.current.y;
    canvas.relativePan({ x: dx, y: dy });
    canvas.requestRenderAll();
    lastPosRef.current = { x, y };
  }, [isPanning]);

  const handlePanMouseUp = useCallback(() => {
    if (isPanning) {
      setIsPanning(false);
    }
  }, [isPanning]);

  /**
   * Attach mouse event handlers
   */
  useEffect(() => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;

    const handleMouseDown = (opt) => {
      const isTouch =
        opt.e.pointerType === 'touch' ||
        (typeof TouchEvent !== 'undefined' && opt.e instanceof TouchEvent);
      // Single-finger touch pan: only in edit mode (isClosed), not during drawing
      if (isTouch && !opt.target && isClosed) {
        setIsPanning(true);
        const { x, y } = getEventCoords(opt.e);
        lastPosRef.current = { x, y };
        return;
      }
      // Touch drawing: defer point placement to lift (mouse:up) so it can be dragged
      // into position first — touch has no hover to preview before a tap (#747).
      if (isTouch && !opt.target && !isClosed && !spacePressed) {
        const canvas = fabricCanvasRef.current;
        if (canvas) {
          const pointer = canvas.getScenePoint(opt.e);
          mousePositionRef.current = pointer;
          updatePreviewLine(pointer);
          touchDrawPendingRef.current = true;
        }
        return;
      }
      if (spacePressed) {
        handlePanMouseDown(opt);
      } else {
        handleCanvasClick(opt);
      }
    };

    const handleMouseMove = (opt) => {
      if (isPanning) {
        handlePanMouseMove(opt);
      } else {
        handleCanvasMouseMove(opt);
      }
    };

    const handleMouseUp = (opt) => {
      if (isPanning) {
        handlePanMouseUp(opt);
        return;
      }
      // Commit a deferred touch point at the lift position (#747).
      if (touchDrawPendingRef.current) {
        touchDrawPendingRef.current = false;
        if (mousePositionRef.current) {
          handleCanvasClick(opt, mousePositionRef.current);
        }
      }
    };

    canvas.on('mouse:down', handleMouseDown);
    canvas.on('mouse:move', handleMouseMove);
    canvas.on('mouse:up', handleMouseUp);

    return () => {
      canvas.off('mouse:down', handleMouseDown);
      canvas.off('mouse:move', handleMouseMove);
      canvas.off('mouse:up', handleMouseUp);
    };
  }, [handleCanvasClick, handleCanvasMouseMove, handlePanMouseDown, handlePanMouseMove, handlePanMouseUp, spacePressed, isPanning, isClosed, updatePreviewLine]);

  /**
   * Handle keyboard events (Ctrl key for snap override, Space for panning)
   */
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Check if user is typing in an input field
      const isTyping = ['INPUT', 'TEXTAREA'].includes(document.activeElement?.tagName) ||
                       document.activeElement?.contentEditable === 'true';
      if (isTyping) return;

      if (e.key === 'Control') {
        setCtrlPressed(true);
      }

      // Space key for panning
      if (e.key === ' ') {
        e.preventDefault();
        setSpacePressed(true);
      }

      // Enter key to close polygon (if >= 3 vertices)
      if (e.key === 'Enter' && vertices.length >= 3 && !isClosed) {
        setIsClosed(true);
        setIsDrawing(false);
        setRebuildTrigger((v) => v + 1); // Force full rebuild
        hidePreview();

        updateOutline({
          vertices,
          isClosed: true,
        });
      }
    };

    const handleKeyUp = (e) => {
      if (e.key === 'Control') {
        setCtrlPressed(false);
      }

      // Space key released
      if (e.key === ' ') {
        setSpacePressed(false);
        setIsPanning(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [vertices, isClosed, updateOutline, hidePreview]);

  /**
   * Handle "Undo Last Point" button
   */
  const handleUndoLastPoint = () => {
    if (vertices.length === 0) return;

    const newVertices = removeLastVertex(vertices);
    setVertices(newVertices);
    setRebuildTrigger((v) => v + 1); // Force full rebuild

    if (newVertices.length === 0) {
      hidePreview();
    }

    updateOutline({
      vertices: newVertices,
      isClosed: false,
    });
  };

  /**
   * Handle "Clear and Restart" button
   */
  const handleClearAndRestart = () => {
    setVertices([]);
    setIsClosed(false);
    setIsDrawing(true);
    setRebuildTrigger((v) => v + 1); // Trigger rebuild (renders empty state, sets skipTargetFind)
    mousePositionRef.current = null;
    nearFirstVertexRef.current = false;
    setNearFirstVertex(false);

    updateOutline({
      vertices: [],
      isClosed: false,
    });

    hidePreview();
  };

  /**
   * Two-finger pinch-to-zoom on touch devices (#746). Fabric dropped its
   * `touch:gesture` event after v5 — pinch is wired onto native touchmove.
   */
  useEffect(() => {
    return attachPinchZoom(fabricCanvasRef.current);
  }, []);

  /**
   * Cancel an active pan when a second finger is placed.
   */
  useEffect(() => {
    const canvas = fabricCanvasRef.current;
    const upper = canvas?.upperCanvasEl;
    if (!upper) return;
    const onTouchStart = (e) => {
      if ((e.touches?.length ?? 0) >= 2) {
        if (isPanning) setIsPanning(false);
        if (touchDrawPendingRef.current) {
          touchDrawPendingRef.current = false; // #747: cancel a deferred point on pinch
          mousePositionRef.current = null;
          // Collapse the preview edge onto the last vertex (no dangling line)
          // instead of toggling `visible` — the reused dashed Line does not
          // reliably reappear after a mid-draw visibility flip, so it stayed gone
          // for later points. Geometry updates (like the live drag) always render.
          const verts = verticesRef.current;
          if (verts.length > 0 && previewLineRef.current) {
            const last = verts[verts.length - 1];
            previewLineRef.current.set({ x1: last.x, y1: last.y, x2: last.x, y2: last.y });
          }
          previewLabelRef.current?.set({ visible: false });
          fabricCanvasRef.current?.requestRenderAll();
        }
      }
    };
    upper.addEventListener('touchstart', onTouchStart, { passive: true });
    return () => upper.removeEventListener('touchstart', onTouchStart);
  }, [isPanning]);

  /**
   * Zoom functions (matching GardenCanvas design)
   */
  const handleZoomIn = () => {
    if (!fabricCanvasRef.current) return;
    const canvas = fabricCanvasRef.current;
    let zoom = canvas.getZoom() * 1.1;
    if (zoom > 5) zoom = 5; // Max 500%
    canvas.setZoom(zoom);
    canvas.requestRenderAll();
  };

  const handleZoomOut = () => {
    if (!fabricCanvasRef.current) return;
    const canvas = fabricCanvasRef.current;
    let zoom = canvas.getZoom() * 0.9;
    if (zoom < 0.1) zoom = 0.1; // Min 10%
    canvas.setZoom(zoom);
    canvas.requestRenderAll();
  };

  const handleZoomReset = () => {
    if (!fabricCanvasRef.current) return;
    const canvas = fabricCanvasRef.current;
    canvas.setZoom(1);
    canvas.viewportTransform = [1, 0, 0, 1, 0, 0];
    canvas.requestRenderAll();
  };

  // Calibration scale forwarded to GardenDimensionsPanel; the panel owns the
  // actual dimensions computation and live-render cadence (#454).
  const calibrationScale = gardenData.backgroundImage?.calibration?.scale;

  // Mobile/tablet: simplified slider view instead of Fabric.js canvas.
  // Show slider when: phone/small screen (isMobile) OR coarse pointer + viewport < 1024 px
  // (covers iPad portrait). iPad landscape (≥1024px) gets the canvas with touch-pan.
  const showSliderFallback = isMobile || (isCoarsePointer && window.innerWidth < 1024);
  if (showSliderFallback) {
    const savedRect = gardenData.outline?.rect || { width: 10, depth: 10 };
    return <Step3MobileView
      savedRect={savedRect}
      updateOutline={updateOutline}
      nextStep={nextStep}
    />;
  }

  return (
    <div className="w-full mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Pencil size={20} className="text-gray-700" />
        <h2 className="text-xl font-bold text-gray-900">Gartenumrisse zeichnen</h2>
      </div>

      {/* Description */}
      <p className="text-sm text-gray-600">
        Klicken Sie, um die Umrisse Ihres Gartens zu zeichnen. Klicken Sie auf den ersten Punkt
        oder drücken Sie Enter, um das Polygon zu schließen.
      </p>

      {/* Info Badge with Controls */}
      <div className="flex items-start gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
        <Info size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1 space-y-3">
          <p className="text-sm text-blue-800">
            {isDrawing
              ? 'Klicken Sie, um Eckpunkte zu setzen. Mindestens 3 Punkte erforderlich.'
              : 'Polygon geschlossen. Sie können Punkte durch Ziehen anpassen.'}
          </p>

          {/* Control Buttons */}
          <div className="flex gap-2 flex-wrap">
            {/* Snap-to-grid toggle — touch-operable alternative to the Ctrl override (#752) */}
            <SnapToggleButton />

            {/* Toggle Background Image */}
            {gardenData.backgroundImage?.dataUrl && (
              <button
                onClick={() => setShowBackgroundImage(!showBackgroundImage)}
                className={`px-3 py-1 text-xs font-medium rounded-md transition-colors flex items-center gap-1 ${
                  showBackgroundImage
                    ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
                title="Hintergrundbild ein-/ausblenden"
              >
                {showBackgroundImage ? (
                  <>
                    <Eye size={14} />
                    <span>Bild sichtbar</span>
                  </>
                ) : (
                  <>
                    <EyeOff size={14} />
                    <span>Bild ausgeblendet</span>
                  </>
                )}
              </button>
            )}

            {/* Undo Last Point - Only show while drawing */}
            {!isClosed && vertices.length > 0 && (
              <button
                onClick={handleUndoLastPoint}
                className="px-3 py-1 text-xs font-medium rounded-md bg-gray-200 text-gray-700
                           hover:bg-gray-300 transition-colors flex items-center gap-1"
                title="Letzten Punkt rückgängig machen"
              >
                <Undo size={14} />
                <span>Letzten Punkt rückgängig</span>
              </button>
            )}

            {/* Clear and Restart - Always visible */}
            {vertices.length > 0 && (
              <button
                onClick={handleClearAndRestart}
                className="px-3 py-1 text-xs font-medium rounded-md bg-red-100 text-red-700
                           hover:bg-red-200 transition-colors flex items-center gap-1"
                title="Alles löschen und neu starten"
              >
                <RotateCcw size={14} />
                <span>Neu starten</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Status Display — isolated component, ticks live during vertex-drag (#454) */}
      <GardenDimensionsPanel
        ref={dimensionsPanelRef}
        vertices={vertices}
        verticesRef={verticesRef}
        isClosed={isClosed}
        calibrationScale={calibrationScale}
      />

      {/* Drawing hints — snap state + proximity hint to close-polygon */}
      <div className="space-y-2">
        {snapEnabled && (
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">Raster:</span>
            <span className="text-sm text-gray-600">
              {gridSize} cm {ctrlPressed && '(Temporär deaktiviert)'}
            </span>
          </div>
        )}
        {/* Fixed height container for proximity hint to prevent jumping */}
        <div className="min-h-[28px]">
          {nearFirstVertex && !isClosed && (
            <div className="text-sm text-emerald-600 font-bold">
              Klicken Sie auf den ersten Punkt, um das Polygon zu schließen
            </div>
          )}
        </div>
      </div>

      {/* Canvas with Zoom Controls — sized via wrapper so the Fabric surface
          stays inside the dialog viewport on every laptop resolution (#450). */}
      <div
        ref={canvasWrapperRef}
        className="relative border-2 border-dashed border-gray-300 rounded-lg overflow-hidden"
        style={{
          width: '100%',
          height: 'min(65vh, 600px)',
          minHeight: '300px',
          backgroundColor: '#F9FAFB', // Gray-50 backdrop (canvas is transparent)
        }}
      >
        <canvas ref={canvasRef} />

        {/* Zoom Controls - Lower Right (matching GardenCanvas design) */}
        <div style={{
          position: 'absolute',
          bottom: '16px',
          right: '16px',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          backgroundColor: 'white',
          padding: '8px',
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          zIndex: 10,
        }}>
          <button
            onClick={handleZoomIn}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#F3F4F6';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'white';
            }}
            style={{
              width: '32px',
              height: '32px',
              border: '1px solid #E5E7EB',
              borderRadius: '4px',
              backgroundColor: 'white',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: 'bold',
              transition: 'all 0.2s',
            }}
            aria-label="Zoom in"
          >
            +
          </button>
          <button
            onClick={handleZoomOut}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#F3F4F6';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'white';
            }}
            style={{
              width: '32px',
              height: '32px',
              border: '1px solid #E5E7EB',
              borderRadius: '4px',
              backgroundColor: 'white',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: 'bold',
              transition: 'all 0.2s',
            }}
            aria-label="Zoom out"
          >
            −
          </button>
          <button
            onClick={handleZoomReset}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#F3F4F6';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'white';
            }}
            style={{
              width: '32px',
              height: '32px',
              border: '1px solid #E5E7EB',
              borderRadius: '4px',
              backgroundColor: 'white',
              cursor: 'pointer',
              fontSize: '10px',
              transition: 'all 0.2s',
            }}
            aria-label="Reset zoom"
          >
            1:1
          </button>
        </div>
      </div>

      {/* Instructions */}
      <div className="text-xs text-gray-500 space-y-1">
        <p>
          <strong>Tastenkombinationen:</strong>
        </p>
        <ul className="list-disc list-inside space-y-1 ml-2">
          <li>Strg gedrückt halten: Raster temporär deaktivieren</li>
          <li>Leertaste: Canvas verschieben (Pan)</li>
          <li>Enter: Polygon schließen (ab 3 Punkten)</li>
          <li>Nach dem Schließen: Punkte durch Ziehen anpassen</li>
        </ul>
      </div>
    </div>
  );
};

export default Step3GardenOutline;
